import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin, Menu, Smartphone, X } from "lucide-react";

const NAV = [
  { label: "HOME", to: "/", testid: "nav-link-home" },
  { label: "LE NOSTRE SEDI", to: "/#sedi", testid: "nav-link-sedi" },
  { label: "SERVIZI", to: "/servizi", testid: "nav-link-servizi" },
  { label: "CONTATTI", to: "/contatti", testid: "nav-link-contatti" },
];

const Logo = ({ light = false }) => (
  <span className="flex flex-col leading-none">
    <span
      className={`font-display text-xl font-extrabold tracking-tight ${
        light ? "text-white" : "text-ink"
      }`}
    >
      SISAL&nbsp;STORE<span className="text-brand">.</span>
    </span>
    <span
      className={`mt-1 text-[10px] font-medium uppercase tracking-[0.22em] ${
        light ? "text-slate-300" : "text-slate-400"
      }`}
    >
      Mestre · Padova · Sant'Angelo di Piove di Sacco
    </span>
  </span>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 border-b bg-white/90 backdrop-blur-md transition-shadow duration-300 ${
          scrolled ? "border-slate-100 shadow-[0_6px_24px_-12px_rgba(15,23,42,0.15)]" : "border-transparent"
        }`}
      >
        <div className="container-x flex h-16 items-center justify-between sm:h-20">
          <Link to="/" data-testid="site-logo" aria-label="Sisal Store — Home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-7 lg:flex" aria-label="Navigazione principale">
            {NAV.map((item) => (
              <NavLink
                key={item.testid}
                to={item.to}
                data-testid={item.testid}
                className="font-display text-[13px] font-semibold tracking-[0.08em] text-slate-600 transition-colors duration-200 hover:text-brand-dark"
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/conto-online"
              data-testid="nav-link-conto-online"
              className="btn btn-primary btn-sm"
            >
              <Smartphone className="h-4 w-4" />
              APRI CONTO ONLINE
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(true)}
            aria-label="Apri il menu"
            className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand hover:text-brand-dark lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] flex flex-col bg-white"
          >
            <div className="container-x flex h-16 items-center justify-between sm:h-20">
              <Logo />
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Chiudi il menu"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 text-ink"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="container-x flex flex-1 flex-col justify-center gap-2" aria-label="Menu mobile">
              {[...NAV.slice(0, 3), { label: "APRI CONTO ONLINE", to: "/conto-online", testid: "mobile-nav-link-conto-online" }, { label: "PROMOZIONI", to: "/promozioni", testid: "mobile-nav-link-promozioni" }, NAV[3]].map((item, i) => (
                <motion.div
                  key={item.testid}
                  initial={{ opacity: 0, x: -18 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.4, ease: [0.215, 0.61, 0.355, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={item.testid.replace("nav-link", "mobile-nav-link")}
                    className={`block border-b border-slate-100 py-4 font-display text-2xl font-bold tracking-tight ${
                      item.to === "/conto-online" ? "text-brand-dark" : "text-ink"
                    }`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            <div className="container-x grid grid-cols-2 gap-3 pb-10">
              <Link to="/#sedi" data-testid="mobile-menu-find-store" className="btn btn-outline btn-md">
                <MapPin className="h-4 w-4" />
                TROVA NEGOZIO
              </Link>
              <Link to="/conto-online" data-testid="mobile-menu-open-account" className="btn btn-primary btn-md">
                <Smartphone className="h-4 w-4" />
                CONTO ONLINE
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
